import React from 'react';
import {
  FileText, AlertCircle, CheckCircle2, Loader2, RefreshCw,
  Wand2, CalendarClock, Hash, ChevronRight,
} from 'lucide-react';
import { type Paper, STATUS_META } from './types';

interface Props {
  paper: Paper;
  onOpen: () => void;
  onReprocess: () => void;
  onGenerate: () => void;
}

/** A single past-paper card in the library grid. */
export const PaperCard: React.FC<Props> = ({
  paper, onOpen, onReprocess, onGenerate,
}) => {
  const meta = STATUS_META[paper.status];
  const isReady = paper.status === 'ready';
  const isFailed = paper.status === 'failed';

  const stopAnd = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
      className="
        group flex cursor-pointer flex-col gap-3
        rounded-2xl border border-slate-200 bg-white p-4
        transition hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(15,23,35,0.07)]
      "
    >
      {/* Head: status + action */}
      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider ${meta.tw}`}>
          {isFailed
            ? <AlertCircle size={11} />
            : isReady
              ? <CheckCircle2 size={11} />
              : <Loader2 size={11} className="animate-spin" />}
          {meta.label}
        </span>
        {isReady && (
          <button
            type="button"
            onClick={stopAnd(onGenerate)}
            title="Generate questions from this paper"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1a5276]/10 text-[#1a5276] hover:bg-[#1a5276]/20"
          >
            <Wand2 size={13} />
          </button>
        )}
        {isFailed && (
          <button
            type="button"
            onClick={stopAnd(onReprocess)}
            title="Retry processing"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1a5276]/10 text-[#1a5276] hover:bg-[#1a5276]/20"
          >
            <RefreshCw size={13} />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${meta.tw}`}>
          <FileText size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p
            className="m-0 truncate leading-none text-slate-900"
            style={{ fontFamily: "'Instrument Serif', serif", fontSize: 19 }}
          >
            {paper.course_code || 'Untitled'}
          </p>
          <p className="m-0 mt-1 truncate text-[12.5px] text-slate-500">
            {paper.file_name}
          </p>
          <p className="m-0 mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-400">
            {paper.year && (<><CalendarClock size={10} /> <span>{paper.year}</span></>)}
            {paper.paper_type && <span>· {paper.paper_type}</span>}
            {paper.chunk_count != null && (<><span>·</span> <Hash size={10} /> <span>{paper.chunk_count} chunks</span></>)}
          </p>
        </div>
      </div>

      {/* Foot */}
      <div className="flex items-center justify-between border-t border-dashed border-slate-200 pt-2 text-[11.5px] text-slate-500">
        <span>
          {paper.question_count != null
            ? `${paper.question_count} questions extracted`
            : 'Processing…'}
        </span>
        <span className="inline-flex items-center gap-0.5 font-bold text-[#1a5276] transition group-hover:translate-x-0.5">
          Open <ChevronRight size={11} />
        </span>
      </div>
    </article>
  );
};
